import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Formik, Form, FormikHelpers} from 'formik';
import Modal from 'shared/components/modal';
import Label from 'shared/components/label';
import TextButton from 'src/components/TextButton';
import {checkPhoneCode} from 'shared/utils';
import {formatMobile} from 'src/utils/formatters';
import {openModal, closeModal} from 'shared/actions/modal';
import {flashMessage} from 'shared/actions/notifications';
import {
  verifyPhoneNumber as verifyPhoneNumberAction,
  PHONE_NUMBER_VERIFIED,
} from 'shared/actions/verify-mobile';
import {Store} from 'src/types/store';
import FormikTextInput from '../inputs/formik-text-input';

interface Props {
  showSettingsGuidance?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface FormValues {
  verification_code: string;
}

const validate = (values: FormValues) => {
  const errors: Partial<FormValues> = {};
  if (!values.verification_code || values.verification_code.length !== 4) {
    errors.verification_code = 'Verification code must be 4 digits long';
  }
  return errors;
};

const defaultOnSuccess = (dispatch) => {
  dispatch(closeModal('VERIFY_PHONE_MODAL'));
  dispatch(flashMessage('Your phone number has been verified and saved.'));
};
const defaultOnCancel = (dispatch) => {
  dispatch(closeModal('VERIFY_PHONE_MODAL'));
  dispatch(openModal('ARE_YOU_SURE_MODAL'));
};

const VerifyPhoneModal = ({showSettingsGuidance = false, onSuccess, onCancel}: Props) => {
  const dispatch = useDispatch();
  const show = useSelector((state: Store) => state.modal['VERIFY_PHONE_MODAL'] || false);
  const mobile = useSelector((state: Store) => state.verifyMobile || '');

  const handleVerifyCall = () => {
    dispatch(verifyPhoneNumberAction(mobile, 'call'));
  };

  const handleSuccess = () => {
    dispatch(PHONE_NUMBER_VERIFIED());
    if (onSuccess) {
      onSuccess();
    } else {
      defaultOnSuccess(dispatch);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      defaultOnCancel(dispatch);
    }
  };

  const handleSubmit = async (
    values: FormValues,
    {setSubmitting, setFieldError}: FormikHelpers<FormValues>
  ) => {
    try {
      const response = await checkPhoneCode(mobile, values.verification_code);
      if (response.success) {
        handleSuccess();
      } else {
        setFieldError('verification_code', response.errors.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onClose={handleCancel}>
      <Formik initialValues={{verification_code: ''}} validate={validate} onSubmit={handleSubmit}>
        {({isSubmitting, values}) => (
          <Form data-testid="verify-phone-form">
            <Modal.TextHeader onClose={handleCancel}>Enter your verification code</Modal.TextHeader>
            <Modal.Body>
              <p>
                {' '}
                A one-time code has been sent to {formatMobile(mobile)}. Please enter your
                verification code to enable alerts.
              </p>
              <Label>
                Code
                <FormikTextInput name="verification_code" placeholder="Code" required />
              </Label>
              <br />
              <p>
                <span className="shared-font bold">Didn&apos;t receive a code? </span>
                Double check the number you entered
                {showSettingsGuidance && ' and make any updates in Account Settings'}. You can also{' '}
                <TextButton onClick={handleVerifyCall} margin={false}>
                  resend as a call.
                </TextButton>
              </p>
            </Modal.Body>
            <Modal.Footer
              primaryButton={{
                text: isSubmitting ? 'Verifying...' : 'Verify',
                type: 'submit',
                disabled: isSubmitting || !values.verification_code,
              }}
              secondaryButton={{
                text: 'Cancel',
                onClick: handleCancel,
                rightAligned: true,
              }}
            />
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default VerifyPhoneModal;
